import { Avatar, Layout, Menu, Skeleton } from "antd";
import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames/bind";
import styles from "./SiderBar.module.scss";
import { appRoutes } from "../../routes/web";
import { toggleOpenKey, toggleSelectedKey } from "../../redux/SideBarSlice";
import LabelMenu from "./LabelMenu/LabelMenu";
import IconItem from "./ItemIcon/ItemIcon";
import logo from "./../../assets/logo.png";
import useResponsive, { SIDER_WIDTH } from "../../hooks/useResponsive";

const { Sider } = Layout;
const cx = classnames.bind(styles);

const ROLE_LABELS = {
  admin: "Quản trị viên",
  supporter: "Hỗ trợ",
  partner: "Đối tác",
  child_partner: "Đối tác con",
};

const getUserInitials = (user) => {
  const source = user?.name || user?.username || "U";
  const parts = source.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const SiderBar = ({ collapsed, isMobile, onMenuNavigate }) => {
  const { user: userData, loading } = useAuth();
  const { defaultSelectedKey, defaultOpenKey } = useSelector(
    (state) => state.sider
  );
  const { siderCollapsedWidth } = useResponsive();
  const dispatch = useDispatch();

  const setKeyActive = useCallback(
    (key, openKey = null) => {
      if (onMenuNavigate) {
        onMenuNavigate();
      }
      if (openKey) {
        dispatch(toggleOpenKey({ defaultOpenKey: key }));
        dispatch(toggleSelectedKey({ defaultSelectedKey: openKey }));
      } else {
        dispatch(toggleSelectedKey({ defaultSelectedKey: key }));
        dispatch(toggleOpenKey({ defaultOpenKey: null }));
      }
    },
    [dispatch, onMenuNavigate]
  );

  const hasPermission = useCallback(
    (route) => {
      if (!userData) return false;

      const hasRole = route.roles?.includes(userData.role);
      if (!route.permissions) return hasRole;

      return (
        hasRole &&
        route.permissions.some((permission) =>
          userData.permissions?.includes(permission)
        )
      );
    },
    [userData]
  );

  const menuItems = useMemo(() => {
    return appRoutes
      .filter((route) => hasPermission(route) && !route.hidden)
      .map((route, index) => {
        if (route.type === "item") {
          return {
            key: route.key,
            label: (
              <LabelMenu>
                <Link to={route.path} onClick={() => setKeyActive(route.key)}>
                  {route.label}
                </Link>
              </LabelMenu>
            ),
            icon: <IconItem>{route.icon}</IconItem>,
          };
        }

        if (route.type === "children") {
          const allowedChildren = route.children.filter(
            (child) =>
              !child.permissions ||
              child.permissions.some((permission) =>
                userData.permissions?.includes(permission)
              )
          );

          if (allowedChildren.length === 0) return null;

          return {
            key: route.key,
            label: <LabelMenu>{route.label}</LabelMenu>,
            icon: <IconItem>{route.icon}</IconItem>,
            children: allowedChildren.map((item, childIndex) => ({
              ...item,
              label: (
                <Link
                  key={`${index}-${childIndex}`}
                  to={item.path}
                  onClick={() => setKeyActive(route.key, item.key)}
                >
                  {item.label}
                </Link>
              ),
            })),
          };
        }

        return {
          type: route.type,
          label: route.label,
        };
      })
      .filter(Boolean);
  }, [userData, setKeyActive, hasPermission]);

  const siderClassName = cx("sider", {
    "sider-collapsed": collapsed && !isMobile,
    "sider-mobile": isMobile,
    "sider-mobile-open": isMobile && !collapsed,
  });

  const renderBrand = () => (
    <div className={cx("brand")}>
      <div className={cx("brand-logo")}>
        <img src={logo} alt="GatePay" />
      </div>
      {!collapsed && (
        <div className={cx("brand-text")}>
          <span className={cx("brand-name")}>GatePay</span>
          <span className={cx("brand-tagline")}>Payment Gateway</span>
        </div>
      )}
    </div>
  );

  const renderUserFooter = () => {
    if (!userData) return null;

    const roleLabel = ROLE_LABELS[userData.role] || userData.role;

    return (
      <div className={cx("user-footer", { collapsed })}>
        <Avatar className={cx("user-avatar")} size={collapsed ? 36 : 40}>
          {getUserInitials(userData)}
        </Avatar>
        {!collapsed && (
          <div className={cx("user-info")}>
            <span className={cx("user-name")}>
              {userData.name || userData.username}
            </span>
            <span className={cx("user-role")}>{roleLabel}</span>
          </div>
        )}
      </div>
    );
  };

  const renderMenu = () => {
    if (loading) {
      return (
        <div className={cx("menu-skeleton")}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton.Button
              key={i}
              active
              block
              size="small"
              style={{ height: 40, marginBottom: 8 }}
            />
          ))}
        </div>
      );
    }

    return (
      <Menu
        className={cx("menu-sider-bar")}
        theme="dark"
        mode="inline"
        selectedKeys={defaultSelectedKey ? [defaultSelectedKey] : []}
        openKeys={defaultOpenKey ? [defaultOpenKey] : []}
        items={menuItems}
      />
    );
  };

  return (
    <Sider
      className={siderClassName}
      trigger={null}
      collapsible
      collapsed={collapsed}
      collapsedWidth={siderCollapsedWidth}
      breakpoint="lg"
      width={SIDER_WIDTH}
    >
      <div className={cx("sider-inner")}>
        {renderBrand()}
        <div className={cx("menu-wrapper")}>{renderMenu()}</div>
        {!loading && renderUserFooter()}
      </div>
    </Sider>
  );
};

export default React.memo(SiderBar);
